/**
=========================================================
* Otis Admin PRO - v2.0.2
=========================================================

* Product Page: https://material-ui.com/store/items/otis-admin-pro-material-dashboard-react/
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Otis Admin PRO React Base Styles
import colors from "assets/theme-dark/base/colors";

// Otis Admin PRO React Helper Functions
import pxToRem from "assets/theme-dark/functions/pxToRem";

const { white } = colors;

const baseProperties = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeightLighter: 100,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  fontSizeXXS: pxToRem(10.4),
  fontSizeXS: pxToRem(12),
  fontSizeSM: pxToRem(14),
  fontSizeMD: pxToRem(16),
  fontSizeLG: pxToRem(18),
  fontSizeXL: pxToRem(20),
  fontSize2XL: pxToRem(24),
  fontSize3XL: pxToRem(30),
};

const baseHeadingProperties = {
  fontFamily: baseProperties.fontFamily,
  color: white.main,
  fontWeight: baseProperties.fontWeightBold,
};

const baseDisplayProperties = {
  fontFamily: baseProperties.fontFamily,
  color: white.main,
  fontWeight: baseProperties.fontWeightLight,
  lineHeight: 1.2,
};

// down('md') = max-width: 767.95px  (md breakpoint = 768)
// down('sm') = max-width: 575.95px  (sm breakpoint = 576)
const md = "@media (max-width:767.95px)";
const sm = "@media (max-width:575.95px)";

const typography = {
  fontFamily: baseProperties.fontFamily,
  fontWeightLighter: baseProperties.fontWeightLighter,
  fontWeightLight: baseProperties.fontWeightLight,
  fontWeightRegular: baseProperties.fontWeightRegular,
  fontWeightMedium: baseProperties.fontWeightMedium,
  fontWeightBold: baseProperties.fontWeightBold,

  h1: {
    fontSize: pxToRem(48),
    lineHeight: 1.25,
    ...baseHeadingProperties,
    [md]: { fontSize: pxToRem(36), lineHeight: 1.3 },
    [sm]: { fontSize: pxToRem(28), lineHeight: 1.357 },
  },

  h2: {
    fontSize: pxToRem(36),
    lineHeight: 1.3,
    ...baseHeadingProperties,
    [md]: { fontSize: pxToRem(28), lineHeight: 1.357 },
    [sm]: { fontSize: pxToRem(22), lineHeight: 1.364 },
  },

  h3: {
    fontSize: pxToRem(30),
    lineHeight: 1.375,
    ...baseHeadingProperties,
    [md]: { fontSize: pxToRem(24), lineHeight: 1.333 },
    [sm]: { fontSize: pxToRem(20), lineHeight: 1.4 },
  },

  h4: {
    fontSize: pxToRem(24),
    lineHeight: 1.375,
    ...baseHeadingProperties,
    [md]: { fontSize: pxToRem(20), lineHeight: 1.4 },
    [sm]: { fontSize: pxToRem(18), lineHeight: 1.444 },
  },

  h5: {
    fontSize: pxToRem(20),
    lineHeight: 1.375,
    ...baseHeadingProperties,
    [md]: { fontSize: pxToRem(18), lineHeight: 1.444 },
    [sm]: { fontSize: pxToRem(16), lineHeight: 1.5 },
  },

  h6: {
    fontSize: pxToRem(16),
    lineHeight: 1.625,
    ...baseHeadingProperties,
    [md]: { fontSize: pxToRem(15), lineHeight: 1.6 },
    [sm]: { fontSize: pxToRem(14), lineHeight: 1.571 },
  },

  subtitle1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.625,
    [md]: { fontSize: baseProperties.fontSizeMD, lineHeight: 1.5 },
    [sm]: { fontSize: baseProperties.fontSizeSM, lineHeight: 1.429 },
  },

  subtitle2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.6,
    [md]: { fontSize: baseProperties.fontSizeSM, lineHeight: 1.429 },
    [sm]: { fontSize: pxToRem(13), lineHeight: 1.385 },
  },

  body1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1,
    [md]: { fontSize: baseProperties.fontSizeMD, lineHeight: 1.5 },
    [sm]: { fontSize: baseProperties.fontSizeSM, lineHeight: 1.429 },
  },

  body2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.6,
    [md]: { fontSize: baseProperties.fontSizeSM, lineHeight: 1.429 },
    [sm]: { fontSize: pxToRem(13), lineHeight: 1.385 },
  },

  button: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.5,
    textTransform: "uppercase",
    [sm]: { fontSize: pxToRem(12), lineHeight: 1.5 },
  },

  caption: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXS,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.25,
    [sm]: { fontSize: pxToRem(11), lineHeight: 1.273 },
  },

  overline: {
    fontFamily: baseProperties.fontFamily,
  },

  d1: {
    fontSize: pxToRem(80),
    ...baseDisplayProperties,
    [md]: { fontSize: pxToRem(60) },
    [sm]: { fontSize: pxToRem(48) },
  },

  d2: {
    fontSize: pxToRem(72),
    ...baseDisplayProperties,
    [md]: { fontSize: pxToRem(56) },
    [sm]: { fontSize: pxToRem(44) },
  },

  d3: {
    fontSize: pxToRem(64),
    ...baseDisplayProperties,
    [md]: { fontSize: pxToRem(48) },
    [sm]: { fontSize: pxToRem(36) },
  },

  d4: {
    fontSize: pxToRem(56),
    ...baseDisplayProperties,
    [md]: { fontSize: pxToRem(44) },
    [sm]: { fontSize: pxToRem(32) },
  },

  d5: {
    fontSize: pxToRem(48),
    ...baseDisplayProperties,
    [md]: { fontSize: pxToRem(36) },
    [sm]: { fontSize: pxToRem(28) },
  },

  d6: {
    fontSize: pxToRem(40),
    ...baseDisplayProperties,
    [md]: { fontSize: pxToRem(30) },
    [sm]: { fontSize: pxToRem(24) },
  },

  size: {
    xxs: baseProperties.fontSizeXXS,
    xs: baseProperties.fontSizeXS,
    sm: baseProperties.fontSizeSM,
    md: baseProperties.fontSizeMD,
    lg: baseProperties.fontSizeLG,
    xl: baseProperties.fontSizeXL,
    "2xl": baseProperties.fontSize2XL,
    "3xl": baseProperties.fontSize3XL,
  },

  lineHeight: {
    sm: 1.25,
    md: 1.5,
    lg: 2,
  },
};

export default typography;
